import { NavLink, Outlet } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuthStore } from "@/stores/auth";
import {
  LayoutDashboard,
  Cpu,
  Rocket,
  Package,
  BookOpen,
  FileText,
  RefreshCw,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useState } from "react";

function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}

function SidebarLink({
  to,
  icon: Icon,
  label,
  collapsed,
}: {
  to: string;
  icon: React.ElementType;
  label: string;
  collapsed?: boolean;
}) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          "flex items-center rounded-lg text-sm font-medium transition-all duration-150",
          collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5",
          isActive
            ? "bg-brand-100 text-brand-400"
            : "text-surface-500 hover:text-surface-700 hover:bg-surface-100"
        )
      }
    >
      <Icon size={18} strokeWidth={1.8} className="flex-shrink-0" />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}

export function AppLayout() {
  const { logout, user: auth0User } = useAuth0();
  const user = useAuthStore((s) => s.user);
  const [collapsed, setCollapsed] = useState(false);

  const displayName =
    auth0User?.given_name || auth0User?.name?.split(" ")[0] || user?.email || "User";
  const avatarInitial =
    (auth0User?.given_name || auth0User?.name || user?.email)?.[0]?.toUpperCase() || "?";

  return (
    <div className="flex h-screen bg-surface-50">
      {/* Sidebar */}
      <aside
        className={cn(
          "bg-surface-0 border-r border-surface-200 flex flex-col transition-all duration-200",
          collapsed ? "w-16" : "w-64"
        )}
      >
        {/* Logo */}
        <div className={cn("py-5 border-b border-surface-200", collapsed ? "px-3" : "px-5")}>
          <div className={cn("flex items-center", collapsed ? "justify-center" : "gap-2.5")}>
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <path d="M4 6 L28 6" stroke="#D4A07A" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
                <path d="M7 12 L25 12" stroke="#D4956C" strokeWidth="2.5" strokeLinecap="round" opacity="0.55"/>
                <path d="M10 18 L22 18" stroke="#C4855C" strokeWidth="3.5" strokeLinecap="round"/>
                <path d="M7 24 L25 24" stroke="#D4956C" strokeWidth="2.5" strokeLinecap="round" opacity="0.55"/>
                <path d="M4 30 L28 30" stroke="#D4A07A" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
              </svg>
            </div>
            {!collapsed && (
              <div>
                <div className="text-sm font-semibold text-surface-900 tracking-tight">
                  SyncStack
                </div>
                <div className="text-[11px] text-surface-400 font-medium font-mono uppercase tracking-wider">
                  PiStack Manager
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className={cn("flex-1 py-4 space-y-1 overflow-y-auto", collapsed ? "px-2" : "px-3")}>
          <SidebarLink to="/fleet" icon={LayoutDashboard} label="Fleet" collapsed={collapsed} />
          <SidebarLink to="/devices" icon={Cpu} label="Devices" collapsed={collapsed} />
          <SidebarLink to="/deploys" icon={Rocket} label="Deploys" collapsed={collapsed} />
          <SidebarLink to="/apps" icon={Package} label="Apps" collapsed={collapsed} />
          <SidebarLink to="/agent" icon={RefreshCw} label="Agent" collapsed={collapsed} />
          <SidebarLink to="/setup" icon={BookOpen} label="Setup Guide" collapsed={collapsed} />
          <SidebarLink to="/docs" icon={FileText} label="Docs" collapsed={collapsed} />
        </nav>

        {/* User footer */}
        <div className={cn("py-4 border-t border-surface-200", collapsed ? "px-2" : "px-3")}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-8 h-8 rounded-full bg-brand-100 text-brand-400 flex items-center justify-center text-xs font-bold"
                title={displayName}
              >
                {avatarInitial}
              </div>
              <button
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                title="Sign out"
                className="flex items-center justify-center w-full px-2 py-2 text-surface-500 hover:text-danger-500 hover:bg-danger-100 rounded-lg transition-colors cursor-pointer"
              >
                <LogOut size={16} strokeWidth={1.8} />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {avatarInitial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-surface-800 truncate">
                    {displayName}
                  </div>
                  <div className="text-[11px] text-surface-400 capitalize">
                    {user?.role || "admin"}
                  </div>
                </div>
              </div>
              <button
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                className="flex items-center gap-2 w-full px-3 py-2 mt-1 text-sm text-surface-500 hover:text-danger-500 hover:bg-danger-100 rounded-lg transition-colors cursor-pointer"
              >
                <LogOut size={16} strokeWidth={1.8} />
                <span>Sign out</span>
              </button>
            </>
          )}
        </div>

        {/* Collapse toggle */}
        <div className={cn("py-2 border-t border-surface-200", collapsed ? "px-2" : "px-3")}>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              "flex items-center w-full py-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors cursor-pointer",
              collapsed ? "justify-center px-2" : "gap-2 px-3"
            )}
          >
            {collapsed ? (
              <PanelLeftOpen size={16} strokeWidth={1.8} />
            ) : (
              <>
                <PanelLeftClose size={16} strokeWidth={1.8} />
                <span className="text-xs font-medium">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
